import { Type } from "class-transformer";
import {InvoiceContent} from "./InvoiceContent";
import {InvoicePayment} from "./InvoicePayment";

export interface InvoiceTaxSubtotal {
  rate: number;
  taxable_amount: number;
  tax_amount: number;
}

const roundCurrency = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export class Invoice {

  constructor() {
    this.payments = [];
    this.contents = [];
    this.vat_reverse_charged = false;
  }

  id!: string;
  name!: string;
  invoice_number?: string;
  tenant!: string;
  quote!: boolean;
  @Type(() => Date)
  created!: Date;
  draft!: boolean;

  @Type(() => Date)
  deadline!: Date;
  deadline_type?: "fixed" | "pay_when_paid";
  customer!: string;
  supplier?: string;
  project_id?: string;
  project_name?: string;
  total!: number;
  tax?: number;
  vat_reverse_charged!: boolean;
  payments!: InvoicePayment[];
  contents!: InvoiceContent[];
  token!: string;

  public CalcTotalContents(): number {
    return this.total;
  }

  public CalcTaxSubtotals(): InvoiceTaxSubtotal[] {
    if (!this.contents?.length) {
      return [];
    }

    const taxableByRate = new Map<number, number>();
    this.contents.forEach(content => {
      const rate = this.vat_reverse_charged || !Number.isFinite(content.tax_rate)
        ? 0
        : content.tax_rate;
      taxableByRate.set(rate, (taxableByRate.get(rate) ?? 0) + content.amount * content.price);
    });

    return Array.from(taxableByRate.entries())
      .sort(([left], [right]) => left - right)
      .map(([rate, taxableAmount]) => ({
        rate,
        taxable_amount: roundCurrency(taxableAmount),
        tax_amount: this.vat_reverse_charged ? 0 : roundCurrency(taxableAmount * rate / 100),
      }));
  }

  public CalcTaxes(): number {
    if (this.vat_reverse_charged) {
      return 0;
    }
    const subtotals = this.CalcTaxSubtotals();
    if (subtotals.length) {
      return roundCurrency(subtotals.reduce((total, subtotal) => total + subtotal.tax_amount, 0));
    }
    return typeof this.tax === "number" && Number.isFinite(this.tax) ? roundCurrency(this.tax) : 0;
  }

  public CalcGrandTotal(): number {
    return roundCurrency(this.CalcTotalContents() + this.CalcTaxes())
  }


  public CalcPendingTotal(): number {
    const total = this.CalcGrandTotal()
    let paymentTotal = 0;
    this.payments.forEach(payment => {
      paymentTotal += payment.amount
    })
    return total - paymentTotal;
  }

  public IsPaid(): boolean {
    let paymentTotal = 0;
    this.payments.forEach(payment => {
      paymentTotal += payment.amount
    })
    return (paymentTotal >= this.CalcGrandTotal()-0.01)
  }

  public GetCreatedDate(): Date {
    return new Date(this.created)
  }
  public GetDeadlineDate(): Date {
    return new Date(this.deadline)
  }

  public GetDisplayName(): string {
    return this.invoice_number || this.name;
  }

  public IsPayWhenPaid(): boolean {
    return this.deadline_type === "pay_when_paid";
  }
}

export class InvoicePaymentPortalInfoTenant {
  id!: string;
  name!: string;
  logo!: string;
}

export class InvoicePaymentPortalInfo {
  id!: string;
  name!: string;
  total!: number;
  paid!: boolean;
  tenant!: InvoicePaymentPortalInfoTenant;
}

export class InvoicePaymentIdealIssuer {
  id!: string;
  name!: string;
  image!: string;
}
